import { Fragment, useMemo, useState } from 'react'
import Icon from './Icon.jsx'
import ContextMenu, { ContextMenuItem, ContextMenuLabel, ContextMenuSeparator } from './ContextMenu.jsx'
import AssetCellEditor from './AssetCellEditor.jsx'
import AssetDowntimeModal from './AssetDowntimeModal.jsx'
import { glyphFor } from '../lib/assets.js'
import { AssetModal } from '../pages/AssetSections.jsx'
import { api } from '../lib/api.js'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext.jsx'
import { formatShort, startOfWeek, weekdayName } from '../lib/dates.js'
import { tintOf } from '../lib/theme.js'

/**
 * The fleet, as a band under the crews.
 *
 * Rows are assets, cells say who has each one that day, resolved by the server (repos/assets.js
 * assetBoard) so nothing here decides precedence — it only draws what the payload says:
 *
 *   standing   the usual operator, drawn quietly: nothing was decided today
 *   exception  somebody else has it today, drawn boldly with a mark: somebody decided
 *   label      an outside party has it ("Go Auto")
 *   parked     nobody has it, on purpose
 *   down       out of service, hatched, with the reason
 *   none       a floater nobody has taken
 *   hidden     somebody this viewer may not see has it
 *   (no entry) not on the fleet that day — a rental before delivery or after return
 *
 * Two exports draw it — FleetWeekRows as extra rows inside the week grid's own table so the
 * columns line up with the days above, and FleetDayTable as its own small table under the
 * day grid — and useFleet holds the editing state and renders the popover and the menus, so
 * neither of the two ever puts a <div> inside a <tbody>.
 */

// A fixed colour, not the tenant's accent — the same reasoning as the CAT colour: the fleet
// band should look like the fleet band in every company that has one.
const FLEET_HUE = '#b45309'
const FLEET_TINT = tintOf(FLEET_HUE, 0.86)

/** assetId:date → the resolved day. */
function indexDays(board) {
  const m = new Map()
  for (const d of board.assetDays || []) m.set(`${d.assetId}:${d.date}`, d)
  return m
}

/** Who is booked on the board on a date — offered first when choosing a holder. */
function onBoardOn(board, date) {
  const ids = new Set()
  for (const a of board.assignments || []) if (a.date === date) ids.add(a.staffId)
  return ids
}

/**
 * Runs of consecutive days on which an asset resolves to the same answer — the same person,
 * the same outside party, parked, or one out-of-service period — for one asset's row. The
 * week grid's sameJobRuns, for the fleet: the run draws as one bar and says its name once,
 * and hovering splits it back into days.
 *
 * @returns Map<dateIso, { key, first, last, len }> — only for dates inside a run of 2 or more.
 */
function sameHolderRuns(days, entryOn, rowKey) {
  const idOf = (e) => {
    if (!e || e.source === 'none') return null
    if (e.source === 'down') return `down|${e.downtimeId}`
    if (e.source === 'hidden') return 'hidden'
    return `${e.source}|${e.staffId || ''}|${e.label || ''}|${e.parked ? 'parked' : ''}`
  }
  const runs = new Map()
  let i = 0
  while (i < days.length) {
    const id = idOf(entryOn(days[i].date))
    if (!id) { i += 1; continue }
    let j = i + 1
    while (j < days.length && idOf(entryOn(days[j].date)) === id) j += 1
    if (j - i >= 2) {
      const key = `${rowKey}:${days[i].date}`
      for (let k = i; k < j; k++) runs.set(days[k].date, { key, first: k === i, last: k === j - 1, len: j - i })
    }
    i = j
  }
  return runs.size ? runs : null
}

/**
 * Stretch a bar's name across the bar. Measured from the run's first and last cell, as the
 * week grid does for its own bars, because the day columns are not a uniform width. A
 * callback ref, so it runs whenever a bar appears; the week grid's resize observer re-measures
 * every .run-label in the table, this one included, when the columns change.
 */
function placeRun(el) {
  if (!el) return
  const first = el.closest('td')
  let last = first
  for (let i = 1; i < Number(el.dataset.runLen || 1); i++) last = last.nextElementSibling || last
  el.style.width = `${last.getBoundingClientRect().right - first.getBoundingClientRect().left}px`
}

function describe(entry, staffById) {
  if (!entry) return { cls: 'offfleet', text: '', title: 'Not on the fleet this day' }
  switch (entry.source) {
    case 'down':
      return { cls: 'down', text: entry.reason ? `Out of service · ${entry.reason}` : 'Out of service' }
    case 'standing':
      return { cls: 'standing', text: staffById.get(entry.staffId)?.name || 'Assigned', title: 'The usual operator' }
    case 'day':
      if (entry.parked) return { cls: 'parked', text: '—', title: 'Parked. Nobody has it today.' }
      if (entry.staffId) return { cls: 'exception', text: staffById.get(entry.staffId)?.name || 'Assigned' }
      return { cls: 'label', text: entry.label }
    case 'hidden':
      return { cls: 'hidden', text: 'Assigned' }
    default:
      return { cls: 'none', text: '' }
  }
}

/**
 * The editing state, and the overlays that go with it. Rendered by the board page outside
 * both tables; the row components only ask it to open things.
 */
export function useFleet({
  board,
  writable,
  canManage,
  onSetDay,
  onClearDay,
  onSaveDowntime,
  onDeleteDowntime,
  onSetCustody,
}) {
  const { tenant, mode: liveMode, assetUnit } = useAuth()
  const [editing, setEditing] = useState(null) // { asset, date, entry, anchor }
  const [menu, setMenu] = useState(null) // { x, y, asset, date, entry }
  const [downtime, setDowntime] = useState(null) // { asset, date, existing }
  // The asset whose register entry is being edited, if any.
  const [editAsset, setEditAsset] = useState(null)
  const qc = useQueryClient()
  /**
   * The register, fetched only once somebody opens the dialog from here.
   *
   * The board's own payload carries an asset's name, description and service window but not
   * its groups' names or its standing operator, and the dialog needs all of it. Sharing the
   * register page's query key means the two never disagree and a second visit costs nothing.
   */
  const { data: register } = useQuery({
    queryKey: ['assets'],
    queryFn: () => api.get('/api/assets'),
    enabled: Boolean(editAsset),
  })
  const staffById = useMemo(() => new Map((board?.staff || []).map((s) => [s.id, s])), [board])

  const open = (asset, date, entry, anchor) => {
    if (!writable || !entry) return
    // Clicking a day that is out of service opens the period that made it so, not a holder
    // picker for a truck that is at the shop.
    if (entry.source === 'down') {
      setDowntime({ asset, date, existing: (board.assetDowntime || []).find((d) => d.id === entry.downtimeId) || null })
      return
    }
    setEditing({ asset, date, entry, anchor })
  }

  const openMenu = (e, asset, date, entry) => {
    if (!writable || !entry) return
    e.preventDefault()
    e.stopPropagation()
    setMenu({ kind: 'cell', x: e.clientX, y: e.clientY, asset, date, entry })
  }

  /**
   * The asset itself, from a right-click on its name — as opposed to one of its days.
   *
   * Only for the people who keep the register: everyone can read the fleet, but its contents
   * are theirs to change, and a menu item that answers with a 403 is worse than no item.
   */
  const openAssetMenu = (e, asset) => {
    if (!canManage) return
    e.preventDefault()
    e.stopPropagation()
    setMenu({ kind: 'asset', x: e.clientX, y: e.clientY, asset })
  }

  // Which cell has something open on it, in the same `asset:date` shape the cells are keyed
  // by. The board above the fleet marks the cell whose editor is showing; a row of trucks
  // that did not was the one place you could lose track of what you were answering.
  const editingKey = editing
    ? `${editing.asset.id}:${editing.date}`
    : downtime
      ? `${downtime.asset.id}:${downtime.date}`
      : null

  const overlays = (
    <>
      {editing && (
        <AssetCellEditor
          asset={editing.asset}
          date={editing.date}
          entry={editing.entry}
          staff={board.staff}
          onBoardIds={onBoardOn(board, editing.date)}
          unitName={assetUnit()}
          anchor={editing.anchor}
          weekStart={tenant?.weekStart || 1}
          today={liveMode?.today}
          onSave={async (patch) => {
            if (await onSetDay({ assetId: editing.asset.id, ...patch })) setEditing(null)
          }}
          onClear={async () => {
            if (await onClearDay(editing.asset.id, editing.date)) setEditing(null)
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      {downtime && (
        <AssetDowntimeModal
          asset={downtime.asset}
          date={downtime.date}
          weekStart={tenant?.weekStart || 1}
          editing={downtime.existing}
          existing={(board.assetDowntime || []).filter((d) => d.assetId === downtime.asset.id)}
          onSave={async (range) => {
            if (await onSaveDowntime(downtime.existing, downtime.asset, range)) setDowntime(null)
          }}
          onDelete={async (id) => {
            if (await onDeleteDowntime(id)) setDowntime(null)
          }}
          onClose={() => setDowntime(null)}
        />
      )}

      {menu?.kind === 'asset' && (
        <ContextMenu x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          <ContextMenuLabel>{menu.asset.name}</ContextMenuLabel>
          <ContextMenuItem
            icon="edit"
            onClick={() => { setEditAsset(menu.asset); setMenu(null) }}
          >
            Edit {assetUnit()}…
          </ContextMenuItem>
        </ContextMenu>
      )}

      {menu?.kind === 'cell' && (
        <ContextMenu x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          <ContextMenuLabel>
            {menu.asset.name} · {weekdayName(menu.date)} {formatShort(menu.date)}
          </ContextMenuLabel>
          {menu.entry.source !== 'down' && (
            <ContextMenuItem
              icon="person"
              onClick={() => {
                setMenu(null)
                setEditing({ asset: menu.asset, date: menu.date, entry: menu.entry, anchor: { top: menu.y, bottom: menu.y, left: menu.x } })
              }}
            >
              Who has it…
            </ContextMenuItem>
          )}
          {menu.entry.source === 'day' && (
            <ContextMenuItem
              icon="back"
              onClick={() => { onClearDay(menu.asset.id, menu.date); setMenu(null) }}
            >
              Back to the usual operator
            </ContextMenuItem>
          )}
          <ContextMenuItem
            icon="alert"
            onClick={() => {
              const existing =
                menu.entry.source === 'down'
                  ? (board.assetDowntime || []).find((d) => d.id === menu.entry.downtimeId) || null
                  : null
              setDowntime({ asset: menu.asset, date: menu.date, existing })
              setMenu(null)
            }}
          >
            {menu.entry.source === 'down' ? 'Edit out of service…' : 'Out of service…'}
          </ContextMenuItem>
          {/* Promoting today's exception to the standing arrangement is a register change,
              so it is offered only to the people who keep the register. */}
          {canManage && menu.entry.source === 'day' && menu.entry.staffId && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem
                icon="pin"
                onClick={() => {
                  onSetCustody(menu.asset, menu.entry.staffId, menu.date)
                  setMenu(null)
                }}
              >
                Make {staffById.get(menu.entry.staffId)?.name || 'them'} the usual operator from {formatShort(menu.date)}
              </ContextMenuItem>
            </>
          )}
        </ContextMenu>
      )}

      {/* The register's own dialog, not a second copy of it — so an asset edited from the
          board and one edited from Settings are the same form with the same rules. Opened
          against the register's copy of the asset, which carries the standing operator and
          the group list the board payload leaves out. */}
      {editAsset && register && (
        <AssetModal
          mode="edit"
          asset={(register.assets || []).find((a) => a.id === editAsset.id) || editAsset}
          groups={register.groups || []}
          staff={register.staff || []}
          unit={assetUnit()}
          onRefresh={() => qc.invalidateQueries({ queryKey: ['assets'] })}
          onClose={() => setEditAsset(null)}
          onSaved={() => {
            setEditAsset(null)
            qc.invalidateQueries({ queryKey: ['assets'] })
            // The board draws this asset too — its name, its service window and which band it
            // sits in all come off the schedule payload.
            qc.invalidateQueries({ queryKey: ['schedule'] })
          }}
        />
      )}
    </>
  )

  return { open, openMenu, openAssetMenu, overlays, staffById, writable, editingKey, canManage }
}

function FleetCell({ entry, fleet, asset, date, className = '', run = null, merged = false, onEnter }) {
  const info = describe(entry, fleet.staffById)
  const editable = fleet.writable && Boolean(entry)
  const title = [info.title, entry?.internalNote].filter(Boolean).join('\n') || undefined
  const runClass = merged ? `run-merged ${run.first ? 'run-first' : ''} ${run.last ? 'run-last' : ''}` : ''
  const isEditing = fleet.editingKey === `${asset.id}:${date}`
  return (
    <td
      className={`fleet-td ${info.cls === 'down' ? 'fleet-down' : ''} ${!entry ? 'fleet-offfleet' : ''} ${className} ${runClass} ${isEditing ? 'editing' : ''}`}
      title={title}
      onMouseEnter={onEnter}
      onClick={(e) => editable && fleet.open(asset, date, entry, e.currentTarget.getBoundingClientRect())}
      onContextMenu={(e) => fleet.openMenu(e, asset, date, entry)}
    >
      {/* The tile keeps its words while merged, painted transparent, so the cell is as wide
          as it will be when the bar splits open. */}
      <div
        className={`fleet-cell ${info.cls} ${editable ? 'editable' : ''}`}
        // An exception is amber, the mark it has always worn; everything else held is neutral.
        style={info.cls === 'exception' ? { '--crew': FLEET_HUE } : undefined}
      >
        <span className="fleet-cell-text">{info.text}</span>
        {entry?.internalNote && <span className="fleet-note-dot" aria-label="Has an internal note" />}
      </div>
      {/* One bar over the whole run, drawn from its first cell; clicks fall through to the
          day underneath. An exception wears the amber mark the tile would have worn. */}
      {merged && run.first && (
        <span
          ref={placeRun}
          className={`run-label fleet-run fleet-run-${info.cls}`}
          data-run-len={run.len}
          style={info.cls === 'exception' ? { '--crew': FLEET_HUE } : undefined}
        >
          {info.text}
        </span>
      )}
    </td>
  )
}

/** The key the ungrouped band answers to. A fixed string, never a real group id. */
export const UNGROUPED = '__ungrouped__'

/**
 * The fleet split into bands, one per asset group, in the register's own order.
 *
 * An asset in two groups appears under both — the same way a borrowed person has a row under
 * two crews on the Group board. Picking one band for it would hide it from a band it genuinely
 * belongs to, and being in several is the whole reason grouping is many-to-many.
 *
 * Anything in no group at all lands in a final band rather than vanishing: an asset added
 * before its group existed is exactly the thing somebody is looking for.
 */
export function buildBands(board, unitName) {
  const assets = board.assets || []
  const groups = board.assetGroups || []
  const bands = groups
    .map((g) => ({
      key: g.id,
      name: g.name,
      icon: g.icon,
      assets: assets.filter((a) => (a.groupIds || []).includes(g.id)),
    }))
    .filter((b) => b.assets.length > 0)

  const loose = assets.filter((a) => !(a.groupIds || []).length)
  if (loose.length) {
    bands.push({ key: UNGROUPED, name: `Other ${unitName}s`, icon: null, assets: loose })
  }
  return bands
}

function FleetHead({ band, count, collapsed, onToggle, colSpan }) {
  // The group's own icon rather than a colour swatch: a group already says what kind of thing
  // it holds, and the glyph says it faster than the word does. The ungrouped band has no icon
  // to show, so it keeps the swatch.
  const Glyph = band.icon ? glyphFor(band.icon) : null
  return (
    <tr className="group-row fleet-head clickable">
      <td colSpan={colSpan} onClick={onToggle} title={collapsed ? `Show ${band.name}` : `Collapse ${band.name}`}>
        <span className="group-row-name">
          <span className="group-head">
            <button
              className={`collapse-btn ${collapsed ? 'collapsed' : ''}`}
              aria-label={collapsed ? `Show ${band.name}` : `Collapse ${band.name}`}
              aria-expanded={!collapsed}
              onClick={(e) => { e.stopPropagation(); onToggle() }}
            >
              <Icon name="down" size={14} strokeWidth={2} />
            </button>
            {Glyph ? (
              <span className="fleet-band-icon"><Glyph size={15} strokeWidth={1.9} /></span>
            ) : (
              <span className="group-swatch" style={{ background: FLEET_HUE }} />
            )}
            {band.name}
            {collapsed && count > 0 && <span className="group-count">{count}</span>}
          </span>
        </span>
      </td>
    </tr>
  )
}

function AssetNameCell({ asset, fleet }) {
  return (
    <td
      className={`staff-cell fleet-asset ${fleet?.canManage ? 'row-click' : ''}`}
      // The register's description — plate, serial, where it lives — where a hover can read it
      // without opening the dialog.
      title={asset.description || undefined}
      onContextMenu={(e) => fleet?.openAssetMenu(e, asset)}
    >
      {asset.name}
    </td>
  )
}

/**
 * Rows for the week and fortnight grids, rendered inside their table so the day columns are
 * the same columns. `days` is the grid's own list of days, so a company that works Saturdays
 * gets six fleet cells a week like everything above.
 */
export function FleetWeekRows({ board, days, fleet, collapsed, onToggleCollapse }) {
  const { tenant, assetUnit } = useAuth()
  const byKey = useMemo(() => indexDays(board), [board])
  const assets = board.assets || []
  const bands = useMemo(() => buildBands(board, assetUnit()), [board, assetUnit])
  // Which merged bar is under the pointer. A td cannot see its siblings' hover, so the row
  // reports it and every cell of the run reads it — the week grid's arrangement.
  const [hoverRun, setHoverRun] = useState(null)
  // The fortnight rule: the first day of the second week, found from the dates, the same way
  // the grid above finds it.
  const weekStart = tenant?.weekStart || 1
  const firstWeek = days.length ? startOfWeek(days[0].date, weekStart) : null
  const splitDate = firstWeek ? days.find((d) => startOfWeek(d.date, weekStart) !== firstWeek)?.date : null

  return (
    <>
      {bands.map((band) => {
        const shut = collapsed.has(band.key)
        return (
          <Fragment key={band.key}>
            <FleetHead
              band={band}
              count={band.assets.length}
              collapsed={shut}
              onToggle={() => onToggleCollapse(band.key)}
              colSpan={days.length + 1}
            />
            {!shut && band.assets.map((asset) => {
              // Keyed by band as well as asset: one asset can sit in two bands, and a run
              // keyed on the asset alone would light both rows up together.
              const rowKey = `${band.key}:${asset.id}`
              const runs = sameHolderRuns(days, (date) => byKey.get(`${asset.id}:${date}`), rowKey)
              return (
                <tr
                  key={rowKey}
                  className="staff-row fleet-row"
                  onMouseLeave={() => setHoverRun((k) => (k && k.startsWith(`${rowKey}:`) ? null : k))}
                >
                  <AssetNameCell asset={asset} fleet={fleet} />
                  {days.map((d) => {
                    const run = runs?.get(d.date) || null
                    return (
                      <FleetCell
                        key={d.date}
                        entry={byKey.get(`${asset.id}:${d.date}`)}
                        fleet={fleet}
                        asset={asset}
                        date={d.date}
                        className={`week-cell ${d.date === splitDate ? 'week-split' : ''}`}
                        run={run}
                        merged={Boolean(run) && hoverRun !== run.key}
                        onEnter={run ? () => setHoverRun(run.key) : undefined}
                      />
                    )
                  })}
                </tr>
              )
            })}
          </Fragment>
        )
      })}
      {assets.length === 0 && (
        <tr className="fleet-row">
          <td className="fleet-empty" colSpan={days.length + 1}>
            No {assetUnit()}s on the fleet yet. Add one under Settings.
          </td>
        </tr>
      )}
    </>
  )
}

/** The day view's fleet: its own small table under the day grid. Asset, and who has it. */
export function FleetDayTable({ board, date, fleet, collapsed, onToggleCollapse }) {
  const { assetsName, assetUnit } = useAuth()
  const byKey = useMemo(() => indexDays(board), [board])
  const assets = board.assets || []
  const bands = useMemo(() => buildBands(board, assetUnit()), [board, assetUnit])

  return (
    <div className="board-wrap fleet-wrap">
      <table className="board fleet-board">
        <thead>
          <tr>
            <th className="col-staff">{assetsName()}</th>
            {/* No caption. The column is one wide cell of names under a heading naming the
                register, and the date is already the board's — it is in the header above,
                and this table only ever draws the day being looked at. */}
            <th />
          </tr>
        </thead>
        <tbody>
          {bands.map((band) => {
            const shut = collapsed.has(band.key)
            return (
              <Fragment key={band.key}>
                <FleetHead
                  band={band}
                  count={band.assets.length}
                  collapsed={shut}
                  onToggle={() => onToggleCollapse(band.key)}
                  colSpan={2}
                />
                {!shut && band.assets.map((asset) => (
                  <tr className="staff-row fleet-row" key={`${band.key}:${asset.id}`}>
                    <AssetNameCell asset={asset} fleet={fleet} />
                    <FleetCell entry={byKey.get(`${asset.id}:${date}`)} fleet={fleet} asset={asset} date={date} />
                  </tr>
                ))}
              </Fragment>
            )
          })}
          {assets.length === 0 && (
            <tr className="fleet-row">
              <td className="fleet-empty" colSpan={2}>
                No {assetUnit()}s on the fleet yet. Add one under Settings.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
